import type { CameraData, Model3D, ModelNode, NodeFile, SecondaryMaterialFileType } from '@/entities/model3d'
import { getGltfLoader } from '@/lib/three-d-resources/gltf-loader'
import type { ModelType } from '@/lib/three-d-resources/model-loader'
import { GLTFMissingError } from '@/lib/three-d-resources/model-loader'
import { exhaustive } from '@/lib/three-d-resources/utils'
import { isLowEndDevice } from '@/lib/utils'
import type { AnimationClip, Object3D, Texture } from 'three'
import {
  AnimationMixer,
  Box3,
  Color,
  Group,
  LinearSRGBColorSpace,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Scene,
  Vector3,
} from 'three'
import { applyBasicLighting, applyDefaultLighting } from '@/lib/threejs/lighting'

const COMPATIBLE_FORMATS = Object.freeze(['gltf_draco', 'gltf_draco_ktx2', 'gltf'])

export const BILLBOARD_TYPE = 'billboard'

export type Layer = {
  title: string
  nodeId: number
  nodeName: string
  layerNumber: number
  color?: string
}

export type LayerGroup = {
  name: string
  layers: Layer[]
  isDefault: boolean
  layerNumber: number
}

export class Model {
  readonly id: number
  title: string | null
  private textScale: number
  modelNodes: ModelType[] = []
  readonly apiLayersById: Map<number, ModelNode> = new Map()
  readonly apiCategoriesById: Map<number, ModelNode> = new Map()
  readonly apiNodesById: Map<number, ModelNode> = new Map()
  readonly layerNodesById: Map<number, Object3D> = new Map()
  readonly scene: Scene = new Scene()
  readonly sceneNodesById: Map<number, Object3D> = new Map()
  readonly nodeNameToId: Map<string, number> = new Map()
  readonly nodeIdToName: Map<number, string> = new Map()
  readonly sceneSize = new Vector3()
  readonly billboards: Object3D[] = []
  readonly animationMixers: AnimationMixer[] = []
  boundingBox = new Box3()
  private defaultLighting = new Group()

  constructor(public apiData: Model3D) {
    this.id = apiData.id
    this.title = apiData.title
    this.textScale = apiData.text_scale

    // This light is always on
    applyBasicLighting(this.scene)

    // These lights may be turned off if any GLTF contains a light
    applyDefaultLighting(this.defaultLighting)
    this.scene.add(this.defaultLighting)

    this.buildMapsFromApiData(apiData)
  }

  async addResource(modelNode: ModelType) {
    if (this.modelNodes.map(({ url }) => url).includes(modelNode.url)) return

    // since v1 models can have null parent_id's from the API, we create the apiLayersById map after the model has been loaded
    if (this.apiData.version === 1) {
      modelNode.group.traverse(n => {
        const node = this.apiNodesById.get(n.userData.nodeId)
        if (!node) return
        if (node.is_root && node.category) {
          // category
          this.apiCategoriesById.set(node.id, node)
        } else if (node.is_root) {
          // layer with no category
          this.apiLayersById.set(node.id, node)
        }
        if (n.parent && this.apiCategoriesById.has(n.parent?.userData.nodeId)) {
          // layer with a category
          this.apiLayersById.set(node.id, { ...node, parent_id: n.parent.userData.nodeId })
        }
      })
    }
    this.modelNodes = this.modelNodes.concat(modelNode)

    for (let [name, id] of modelNode.nodeNameToId) {
      const sceneNode = modelNode.sceneNodes.get(id)
      if (sceneNode && name && id) {
        this.sceneNodesById.set(id, sceneNode)
        if (this.apiLayersById.has(id)) {
          this.layerNodesById.set(id, sceneNode)
        }
      }
    }

    await this.loadTextures(modelNode)
    this.loadSecondaryTextures(modelNode, ['EMISSION', 'TRANSPARENT', 'DISPLACEMENT', 'ORM'])
    this.scene.add(modelNode.group)

    // Initialize gltf animations
    if (modelNode.animations.length) {
      const animationMixer = new AnimationMixer(modelNode.group)
      modelNode.animations.forEach((animationClip: AnimationClip) => {
        animationMixer.clipAction(animationClip).play()
      })
      this.animationMixers.push(animationMixer)
    }

    for (const layer of this.layerNodesById.values()) {
      // Track opacities
      _recursivelyStoreMaterialOpacity(layer, 0, this.apiLayersById)
    }

    this.boundingBox.expandByObject(modelNode.group)
    this.boundingBox.getSize(this.sceneSize)

    modelNode.billboardLabels.forEach(billboard => {
      billboard.userData.type = BILLBOARD_TYPE
    })
    this.billboards.push(...modelNode.billboardLabels)

    // If the scene contains any lights, turn off default lighting
    if (modelNode.meta.hasLighting) {
      this.scene.remove(this.defaultLighting)
    }
  }

  getVisibleNodesFiles(camera: CameraData): NodeFile[] {
    return this.getFilesToDownload(node => camera.visible_nodes.includes(node.id))
  }

  getHiddenNodesFiles(camera: CameraData): NodeFile[] {
    return this.getFilesToDownload(node => !camera.visible_nodes.includes(node.id))
  }

  getFilesToDownload(nodeFilter: (node: ModelNode) => boolean): NodeFile[] {
    let toDownload: NodeFile[]
    // should check for existing complete resources and not dispose them
    switch (this.apiData.version) {
      case 2:
        toDownload = this.apiData.nodes
          .filter(node => node.is_layer)
          .filter(nodeFilter)
          .map(node => node.node_files)
          .flatMap(_getCompatibleResource)
        break
      case 1:
        toDownload = [_getLegacyModelResource(this.apiData)]
        break
      default:
        // Typescript will have a static error if we haven't performed an exhaustive check
        exhaustive(this.apiData.version)
    }
    // don't include already-downloaded files
    return toDownload.filter(file => this.modelNodes.every(modelNode => modelNode.nodeId !== file.node_id))
  }

  getTextScale(): number {
    if (this.textScale) {
      return this.textScale
    }
    return 1
  }

  setTextScale(newTextScale: number) {
    this.textScale = newTextScale
  }

  private applyTextureMap(node: Mesh, map: Texture) {
    const { material } = node
    if (!(material instanceof MeshBasicMaterial || material instanceof MeshStandardMaterial)) return

    if (material.map) material.map.dispose()
    material.map = map
    material.map.colorSpace = LinearSRGBColorSpace
    material.needsUpdate = true
  }

  private applyEmissiveMap(node: Mesh, map: Texture) {
    const { material } = node
    if (!(material instanceof MeshStandardMaterial)) return
    if (material.emissiveMap) material.emissiveMap.dispose()
    material.emissiveMap = map
    material.needsUpdate = true
  }

  private applyAlphaMap(node: Mesh, map: Texture) {
    const { material } = node
    if (!(material instanceof MeshStandardMaterial)) return
    if (material.alphaMap) material.alphaMap.dispose()
    material.transparent = true
    material.alphaMap = map
    material.needsUpdate = true
  }

  private applyDisplacementMap(node: Mesh, map: Texture) {
    const { material } = node
    if (!(material instanceof MeshStandardMaterial)) return
    if (material.displacementMap) material.displacementMap.dispose()
    material.displacementMap = map
    material.needsUpdate = true
  }

  private applyORMMap(node: Mesh, map: Texture) {
    const { material } = node
    if (!(material instanceof MeshStandardMaterial)) return
    if (material.metalnessMap) material.metalnessMap.dispose()
    if (material.roughnessMap) material.roughnessMap.dispose()
    if (material.aoMap) material.aoMap.dispose()
    material.metalnessMap = map
    material.roughnessMap = map
    material.metalness = 1
    material.aoMap = map

    material.needsUpdate = true
  }

  private loadSecondaryTextures(modelNode: ModelType, textureTypes: SecondaryMaterialFileType[]) {
    const textureLoader = getGltfLoader().ktx2Loader
    const texturePromises: Promise<void>[] = []
    modelNode.group.traverse((n: Object3D) => {
      const nodeApiData = this.apiData.nodes.find(apiNode => apiNode.id === n.userData.nodeId)
      if (nodeApiData?.materials[0]?.lighting_model !== 'R') return

      if (nodeApiData && n instanceof Mesh) {
        const nodeHasTextures = nodeApiData.materials.length && nodeApiData.materials[0].material_files.length
        if (nodeHasTextures) {
          const matFiles = nodeApiData.materials[0].material_files.filter(f => f.platform === 'web')

          textureTypes.forEach(textureType => {
            const files = matFiles.filter(f => f.type === textureType)
            const [highRes, lowRes] = [files.find(f => f.resolution === 'HIGH')?.file, files.find(f => f.resolution === 'LOW')?.file]

            const applyMethod = {
              EMISSION: this.applyEmissiveMap,
              TRANSPARENT: this.applyAlphaMap,
              DISPLACEMENT: this.applyDisplacementMap,
              ORM: this.applyORMMap,
            }[textureType as string]

            if (!applyMethod) return

            if (lowRes && highRes) {
              texturePromises.push(
                new Promise(resolve => {
                  textureLoader.load(lowRes.url, (lowResMap: Texture) => {
                    applyMethod(n, lowResMap)
                    resolve() // resolving allows the low-res textured node to be added to the scene, but execution continues
                    textureLoader.load(highRes.url, (highResMap: Texture) => {
                      applyMethod(n, highResMap)
                    })
                  })
                }),
              )
            } else {
              if (lowRes ?? highRes) {
                texturePromises.push(
                  new Promise(resolve => {
                    textureLoader.load(highRes?.url ?? lowRes?.url, (map: Texture) => {
                      applyMethod(n, map)
                      resolve()
                    })
                  }),
                )
              }
            }
          })
        }
      }
    })
    return Promise.all(texturePromises)
  }

  private async loadTextures(modelNode: ModelType) {
    const textureLoader = getGltfLoader().ktx2Loader
    const texturePromises: Promise<void>[] = []
    modelNode.group.traverse((n: Object3D) => {
      const nodeApiData = this.apiData.nodes.find(apiNode => apiNode.id === n.userData.nodeId)
      if (nodeApiData && n instanceof Mesh) {
        const nodeHasTextures = nodeApiData.materials.length && nodeApiData.materials[0].material_files.length
        if (nodeHasTextures) {
          const matFiles = nodeApiData.materials[0].material_files.filter(f => f.platform === 'web')

          const files = matFiles.filter(f => f.type === 'DIFFUSE')
          const [diffuseHighRes, diffuseLowRes] = [
            files.find(f => f.resolution === 'HIGH')?.file,
            files.find(f => f.resolution === 'LOW')?.file,
          ]

          if (diffuseLowRes && diffuseHighRes) {
            texturePromises.push(
              new Promise(resolve => {
                textureLoader.load(diffuseLowRes.url, (lowResMap: Texture) => {
                  this.applyTextureMap(n, lowResMap)
                  resolve() // resolving allows the low-res textured node to be added to the scene, but execution continues
                  textureLoader.load(diffuseHighRes.url, (highResMap: Texture) => {
                    this.applyTextureMap(n, highResMap)
                  })
                })
              }),
            )
          } else {
            if (diffuseLowRes ?? diffuseHighRes) {
              texturePromises.push(
                new Promise(resolve => {
                  textureLoader.load(diffuseHighRes?.url ?? diffuseLowRes?.url, (map: Texture) => {
                    this.applyTextureMap(n, map)
                    resolve()
                  })
                }),
              )
            }
          }
        }
      }
    })
    return Promise.all(texturePromises)
  }

  private buildMapsFromApiData(apiData: Model3D) {
    switch (apiData.version) {
      case 2:
        apiData.nodes.forEach(node => {
          this.nodeNameToId.set(node.node_name, node.id)
          this.nodeIdToName.set(node.id, node.node_name)
          if (node.is_layer) {
            this.apiLayersById.set(node.id, node)
          }
          if (node.is_root && !node.is_layer) {
            this.apiCategoriesById.set(node.id, node)
          }
        })
        break
      case 1:
        _legacyBuildApiMaps(apiData, this)
        break
      default:
        exhaustive(apiData.version)
    }

    // @todo: can this be removed? might be needed for layerGroups
    for (const layer of this.apiLayersById.values()) {
      // add category to node based off parent
      if (layer.parent_id) {
        const categoryNode = this.apiCategoriesById.get(layer.parent_id)
        layer.category = categoryNode?.category ?? null
      }
    }
  }
}

function _getLegacyModelResource(model: Model3D): NodeFile {
  const desktopFileUrl = model.gltf_draco_ktx2 || model.gltf_draco || model.gltf
  const mobileFileUrl = model.gltf_mobile_file?.url || desktopFileUrl
  let url: string | null, type: NodeFile['type']
  if (isLowEndDevice()) {
    type = 'gltf_mobile'
    url = mobileFileUrl
  } else {
    type = 'gltf_draco'
    url = desktopFileUrl
  }

  if (!url) {
    throw new GLTFMissingError(`all of ${COMPATIBLE_FORMATS.join(', ')} were null`)
  }

  return {
    type,
    model3d_id: model.id,
    node_id: null,
    file: { url },
  }
}

function _getCompatibleResource(resource: NodeFile[]): NodeFile {
  const compatible = resource.filter(r2 => COMPATIBLE_FORMATS.includes(r2.type))
  if (compatible.length === 0) {
    throw new GLTFMissingError(`all of ${COMPATIBLE_FORMATS.join(', ')} were null`)
  }
  return compatible[0]
}

function _legacyBuildApiMaps(
  apiData: Model3D,
  maps: {
    readonly nodeNameToId: Map<string, number>
    readonly nodeIdToName: Map<number, string>
    readonly apiCategoriesById: Map<number, ModelNode>
    readonly apiLayersById: Map<number, ModelNode>
    readonly apiNodesById: Map<number, ModelNode>
  },
) {
  for (const node of apiData.nodes) {
    maps.nodeNameToId.set(node.node_name, node.id)
    maps.nodeIdToName.set(node.id, node.node_name)
    maps.apiNodesById.set(node.id, node)
    if (node.is_root) {
      if (node.category) {
        maps.apiCategoriesById.set(node.id, node)
      } else {
        // layers with no category
        maps.apiLayersById.set(node.id, node)
      }
    }
  }
  for (const node of apiData.nodes) {
    if (node.parent_id && maps.apiCategoriesById.has(node.parent_id)) {
      // layers with a category
      maps.apiLayersById.set(node.id, node)
    }
  }
}

/**
 * Stores each material's original opacity so that when toggling/transitioning its opacity,
 * it goes to its intended opacity value instead of the default value which was 1 in prior cases
 */
const _recursivelyStoreMaterialOpacity = (obj: Object3D | Mesh | undefined, renderOrder = 0, modelNodes: Map<number, ModelNode>) => {
  // Base case
  if (!obj) return

  for (let child of obj.children) {
    _recursivelyStoreMaterialOpacity(child, renderOrder + 1, modelNodes)
  }

  if (!(obj instanceof Mesh)) return

  // if the node doesn't have a MeshBasicMaterial, we'll set it to an empty Mesh Material
  // to avoid an undefined TypeError
  const material = (Array.isArray(obj.material) ? obj.material[0] : obj.material) ?? new MeshBasicMaterial()
  obj.material ??= material

  if (material.transparent) {
    obj.renderOrder = renderOrder // TODO: Remove? Was this an attempt at fixing the transparent rendering issues?
  }

  const modelNode = modelNodes.get(obj.userData.nodeId)
  obj.userData.originalOpacity = modelNode?.materials?.[0]?.transparency ?? material.opacity
}
